import {
	getInvestorPersonalDetailsByUserId,
	removeInvestorPersonalDetails,
	updateInvestorPersonalDetails,
} from '@dealstack/frontend/shared/dataExchange/services/god.service';
import { logger } from '@dealstack/frontend/shared/mechanism/logger/logger';
import { useDSToast } from '@dealstack/frontend/shared/mechanism/toast/useDSToast';
import { UpdateInvestorPersonalDetailsDto } from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { normalizePhoneNumberForBE } from '@dealstack/shared/lib/format/string';
import { useCommonYupValidation } from '@dealstack/frontend/shared/other/react/form/useCommonYupValidation';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

export enum INVESTOR_PERSONAL_INFO_FORM {
	COUNTRY = 'country',
	CITY = 'city',
	POST_CODE = 'postCode',
	STREET = 'street',
	HOUSE_NUMBER = 'houseNumber',
	TELEPHONE_PREFIX = 'telephonePrefix',
	PHONE_NUMBER = 'phoneNumber',
	BIRTH_DATE = 'birthDate',
}
export enum PIV_FORM {
	PIV_COUNTRY = 'pivCountry',
	PIV_CITY = 'pivCity',
	PIV_POST_CODE = 'pivPostCode',
	PIV_STREET = 'pivStreet',
	PIV_HOUSE_NUMBER = 'pivHouseNumber',
	PIV_NAME = 'pivName',
	PIV_REG_NUMBER = 'pivRegNumber',
}
export enum LEGAL_REP_FORM {
	REP_COUNTRY = 'repCountry',
	REP_CITY = 'repCity',
	REP_POST_CODE = 'repPostCode',
	REP_STREET = 'repStreet',
	REP_HOUSE_NUMBER = 'repHouseNumber',
	REP_TELEPHONE_PREFIX = 'repTelephonePrefix',
	REP_PHONE_NUMBER = 'repPhoneNumber',
	REP_BIRTH_DATE = 'repBirthDate',
	REP_FIRST_NAME = 'repFirstName',
	REP_LAST_NAME = 'repLastName',
	REP_EMAIL = 'repEmail',
}
export const useManageInvestorPersonalDetailsController = () => {
	const toastAPI = useDSToast();
	const { userId } = useParams();
	const navigate = useNavigate();

	const getInvestorDataRequest = useQuery({
		queryKey: [
			formatUnicorn(API_PATHS.GODMODE_INVESTOR_USERID_PERSONAL_DETAILS_GET, {
				userId: userId ?? '',
			}),
		],

		queryFn: ({ signal }) =>
			getInvestorPersonalDetailsByUserId(
				formatUnicorn(API_PATHS.GODMODE_INVESTOR_USERID_PERSONAL_DETAILS_GET, {
					userId: userId ?? '',
				}),
				signal
			),

		enabled: !!userId,
		meta: { suppressErrorToast: true },
	});

	const removeInvestorPersonalDetailsRequest = useMutation({
		mutationFn: removeInvestorPersonalDetails,

		onSuccess() {
			toastAPI.success({
				message: 'Investor personal details was cleared successfully',
			});
			navigate(`../investor/${userId}`);
		},

		onError(error) {
			toastAPI.error({
				message: 'Error while clearing Investor personal details. Please, try again later.',
			});
			logger.error(error);
		},
	});

	const investor = getInvestorDataRequest?.data?.payload;

	const phoneNumber = investor?.phoneNumber
		? investor?.telephonePrefix + investor?.phoneNumber
		: '';

	const repPhoneNumber = investor?.representative?.phoneNumber
		? investor?.representative?.telephonePrefix + investor?.representative?.phoneNumber
		: '';

	const defaultValues = {
		[INVESTOR_PERSONAL_INFO_FORM.COUNTRY]: investor?.address?.country,
		[INVESTOR_PERSONAL_INFO_FORM.CITY]: investor?.address?.city,
		[INVESTOR_PERSONAL_INFO_FORM.STREET]: investor?.address?.street,
		[INVESTOR_PERSONAL_INFO_FORM.HOUSE_NUMBER]: investor?.address?.houseNumber,
		[INVESTOR_PERSONAL_INFO_FORM.POST_CODE]: investor?.address?.postCode,
		[INVESTOR_PERSONAL_INFO_FORM.TELEPHONE_PREFIX]: investor?.telephonePrefix,
		[INVESTOR_PERSONAL_INFO_FORM.PHONE_NUMBER]: phoneNumber,
		[INVESTOR_PERSONAL_INFO_FORM.BIRTH_DATE]: investor?.birthDate ?? '',
		[PIV_FORM.PIV_COUNTRY]: investor?.vehicleData?.address?.country,
		[PIV_FORM.PIV_CITY]: investor?.vehicleData?.address?.city,
		[PIV_FORM.PIV_POST_CODE]: investor?.vehicleData?.address?.postCode,
		[PIV_FORM.PIV_STREET]: investor?.vehicleData?.address?.street,
		[PIV_FORM.PIV_HOUSE_NUMBER]: investor?.vehicleData?.address?.houseNumber,
		[PIV_FORM.PIV_NAME]: investor?.vehicleData?.pivName,
		[PIV_FORM.PIV_REG_NUMBER]: investor?.vehicleData?.corporateNumber,
		[LEGAL_REP_FORM.REP_COUNTRY]: investor?.representative?.address?.country,
		[LEGAL_REP_FORM.REP_CITY]: investor?.representative?.address?.city,
		[LEGAL_REP_FORM.REP_POST_CODE]: investor?.representative?.address?.postCode,
		[LEGAL_REP_FORM.REP_STREET]: investor?.representative?.address?.street,
		[LEGAL_REP_FORM.REP_HOUSE_NUMBER]: investor?.representative?.address?.houseNumber,
		[LEGAL_REP_FORM.REP_TELEPHONE_PREFIX]: investor?.representative?.telephonePrefix,
		[LEGAL_REP_FORM.REP_PHONE_NUMBER]: repPhoneNumber,
		[LEGAL_REP_FORM.REP_BIRTH_DATE]: investor?.representative?.birthDate ?? '',
		[LEGAL_REP_FORM.REP_FIRST_NAME]: investor?.representative?.firstName,
		[LEGAL_REP_FORM.REP_LAST_NAME]: investor?.representative?.lastName,
		[LEGAL_REP_FORM.REP_EMAIL]: investor?.representative?.email,
	};
	const commonYup = useCommonYupValidation();

	const createInvestorPersonalDetailsForm = useForm({
		mode: 'all',
		reValidateMode: 'onChange',
		defaultValues,
		resolver: yupResolver(
			Yup.object().shape({
				[INVESTOR_PERSONAL_INFO_FORM.TELEPHONE_PREFIX]: commonYup.string.optional,
				[INVESTOR_PERSONAL_INFO_FORM.PHONE_NUMBER]: commonYup.phone.optional,
				[LEGAL_REP_FORM.REP_TELEPHONE_PREFIX]: commonYup.string.optional,
				[LEGAL_REP_FORM.REP_PHONE_NUMBER]: commonYup.phone.optional,
			})
		),
	});

	createInvestorPersonalDetailsForm.watch();

	useEffect(() => {
		if (!investor) {
			return;
		}
		createInvestorPersonalDetailsForm.reset(defaultValues);
	}, [investor]);

	const updateInvestorPersonalDetailsRequest = useMutation({
		mutationFn: updateInvestorPersonalDetails,
	});
	const onSubmit = useCallback((formData: UpdateInvestorPersonalDetailsDto) => {
		const personalPhoneNumber = normalizePhoneNumberForBE(
			formData[INVESTOR_PERSONAL_INFO_FORM.PHONE_NUMBER],
			formData[INVESTOR_PERSONAL_INFO_FORM.TELEPHONE_PREFIX]
		);

		const repPhoneNumber = normalizePhoneNumberForBE(
			formData[LEGAL_REP_FORM.REP_PHONE_NUMBER],
			formData[LEGAL_REP_FORM.REP_TELEPHONE_PREFIX]
		);

		const personalBirthDate =
			formData[INVESTOR_PERSONAL_INFO_FORM.BIRTH_DATE] || investor?.birthDate;

		const repBirthDate =
			formData[LEGAL_REP_FORM.REP_BIRTH_DATE] || investor?.representative?.birthDate;

		const updatedData: UpdateInvestorPersonalDetailsDto = {
			[INVESTOR_PERSONAL_INFO_FORM.COUNTRY]: formData[INVESTOR_PERSONAL_INFO_FORM.COUNTRY],
			[INVESTOR_PERSONAL_INFO_FORM.CITY]: formData[INVESTOR_PERSONAL_INFO_FORM.CITY],
			[INVESTOR_PERSONAL_INFO_FORM.STREET]: formData[INVESTOR_PERSONAL_INFO_FORM.STREET],
			[INVESTOR_PERSONAL_INFO_FORM.HOUSE_NUMBER]:
				formData[INVESTOR_PERSONAL_INFO_FORM.HOUSE_NUMBER],
			[INVESTOR_PERSONAL_INFO_FORM.POST_CODE]:
				formData[INVESTOR_PERSONAL_INFO_FORM.POST_CODE],
			[INVESTOR_PERSONAL_INFO_FORM.TELEPHONE_PREFIX]:
				formData[INVESTOR_PERSONAL_INFO_FORM.TELEPHONE_PREFIX],
			[INVESTOR_PERSONAL_INFO_FORM.PHONE_NUMBER]: personalPhoneNumber,
			[INVESTOR_PERSONAL_INFO_FORM.BIRTH_DATE]: personalBirthDate,
			representative: {
				[LEGAL_REP_FORM.REP_COUNTRY]: formData[LEGAL_REP_FORM.REP_COUNTRY],
				[LEGAL_REP_FORM.REP_CITY]: formData[LEGAL_REP_FORM.REP_CITY],
				[LEGAL_REP_FORM.REP_POST_CODE]: formData[LEGAL_REP_FORM.REP_POST_CODE],
				[LEGAL_REP_FORM.REP_STREET]: formData[LEGAL_REP_FORM.REP_STREET],
				[LEGAL_REP_FORM.REP_HOUSE_NUMBER]: formData[LEGAL_REP_FORM.REP_HOUSE_NUMBER],
				[LEGAL_REP_FORM.REP_TELEPHONE_PREFIX]:
					formData[LEGAL_REP_FORM.REP_TELEPHONE_PREFIX],
				[LEGAL_REP_FORM.REP_PHONE_NUMBER]: repPhoneNumber,
				[LEGAL_REP_FORM.REP_BIRTH_DATE]: repBirthDate,
				[LEGAL_REP_FORM.REP_FIRST_NAME]: formData[LEGAL_REP_FORM.REP_FIRST_NAME],
				[LEGAL_REP_FORM.REP_LAST_NAME]: formData[LEGAL_REP_FORM.REP_LAST_NAME],
				[LEGAL_REP_FORM.REP_EMAIL]: formData[LEGAL_REP_FORM.REP_EMAIL],
			},
			vehicleData: {
				[PIV_FORM.PIV_COUNTRY]: formData[PIV_FORM.PIV_COUNTRY],
				[PIV_FORM.PIV_CITY]: formData[PIV_FORM.PIV_CITY],
				[PIV_FORM.PIV_POST_CODE]: formData[PIV_FORM.PIV_POST_CODE],
				[PIV_FORM.PIV_STREET]: formData[PIV_FORM.PIV_STREET],
				[PIV_FORM.PIV_HOUSE_NUMBER]: formData[PIV_FORM.PIV_HOUSE_NUMBER],
				[PIV_FORM.PIV_NAME]: formData[PIV_FORM.PIV_NAME],
				[PIV_FORM.PIV_REG_NUMBER]: formData[PIV_FORM.PIV_REG_NUMBER],
			},
		};
		if (!userId) {
			return;
		}
		updateInvestorPersonalDetailsRequest.mutate(
			{
				userId,
				...updatedData,
			},
			{
				onSuccess: () => {
					toastAPI.success({
						title: 'Investor personal details',
						message: 'Update successfully',
					});
					navigate(`../investor/${userId}`);
				},
			}
		);
	}, []);

	const onClearSubmit = useCallback(() => {
		if (!userId) {
			return;
		}
		removeInvestorPersonalDetailsRequest.mutate({ userId });
	}, [userId]);

	return {
		createInvestorPersonalDetailsForm,
		onSubmit,
		investor,
		userId,
		isLoading: getInvestorDataRequest?.isLoading,
		onClearSubmit,
	};
};
